/**
 * Module View Page for trAInedup.ai
 * Handles viewing of different module types: video, audio, PDF, text, quiz
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ROUTES } from '../utils/constants';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Clock,
  Download,
  FileText,
  Headphones,
  Video,
  HelpCircle,
  BookOpen,
  RotateCcw,
  Send,
  Eye,
  Star,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { courseService } from '../services/firebase/courses';
import { progressService } from '../services/firebase/progress';
import { Course, Module, UserProgress, ModuleType, CourseReviewFeedback } from '../types/course';
import { CourseReviewModule } from '../components/courses/CourseReviewModule';

/**
 * Quiz question interface
 */
interface QuizQuestion {
  id: string;
  question: string;
  type?: string; // Question type (multiple_choice, true_false, short_answer, etc.)
  options: string[];
  correctAnswer: number | boolean | string | string[] | number[];
  explanation?: string;
  leftItems?: string[]; // For matching questions
  rightItems?: string[]; // For matching questions
  blankPlaceholder?: string; // For short answer questions
}

/**
 * Module view page component
 */
export const ModuleViewPage: React.FC = () => {
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { canAccessTier } = useSubscription();
  
  // State
  const [course, setCourse] = useState<Course | null>(null);
  const [module, setModule] = useState<Module | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);

  const courseRef = useRef<Course | null>(null);
  const modulesRef = useRef<Module[]>([]);
  const userProgressRef = useRef<UserProgress | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const sessionModuleRef = useRef<string | null>(null);
  const isComponentMountedRef = useRef(false);
  
  // Quiz state
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: any }>({});
  const [showResults, setShowResults] = useState(false);
  
  // Course review state
  const [showCourseReview, setShowCourseReview] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  // Quiz pass percentage from Firestore (dynamic)
  const [quizPassPercentage, setQuizPassPercentage] = useState<number>(70);

  // Track mount state to avoid state updates after unmounting
  useEffect(() => {
    isComponentMountedRef.current = true;

    return () => {
      isComponentMountedRef.current = false;
    };
  }, []);

  /**
   * End current learning session
   */
  const endCurrentSession = useCallback(async () => {
    if (!sessionIdRef.current) {
      return;
    }

    try {
      await progressService.endLearningSession(sessionIdRef.current);
    } catch (error) {
      console.error('Error ending current session:', error);
    } finally {
      sessionIdRef.current = null;
      sessionModuleRef.current = null;
      if (isComponentMountedRef.current) {
        setSessionStartTime(null);
      }
    }
  }, [setSessionStartTime]);

  /**
   * Load module data
   */
  useEffect(() => {
    let isMounted = true;

    const loadModuleData = async () => {
      if (!courseId || !moduleId) return;

      setLoading(true);

      try {
        let courseData = courseRef.current;
        let modulesData = modulesRef.current;

        if (!courseData || courseData.id !== courseId || modulesData.length === 0) {
          const [fetchedCourse, fetchedModules] = await Promise.all([
            courseService.getCourse(courseId),
            courseService.getCourseModules(courseId),
          ]);

          if (!fetchedCourse) {
            navigate(ROUTES.courses);
            return;
          }

          courseData = fetchedCourse;
          modulesData = fetchedModules;

          if (!isMounted) return;

          courseRef.current = fetchedCourse;
          modulesRef.current = fetchedModules;
          setCourse(fetchedCourse);
          setModules(fetchedModules);
        }

        const currentModules = modulesData.length > 0 ? modulesData : modulesRef.current;
        const cachedModule = currentModules.find(m => m.id === moduleId);

        let resolvedModule = cachedModule ?? null;

        if (!resolvedModule) {
          navigate(`/courses/${courseId}`);
          return;
        }

        if (!resolvedModule.content) {
          try {
            const fullModule = await courseService.getModule(moduleId);
            if (fullModule) {
              resolvedModule = fullModule;
              modulesRef.current = modulesRef.current.map(m => (m.id === moduleId ? fullModule : m));
              if (isMounted) {
                setModules(prev => prev.map(m => (m.id === moduleId ? fullModule : m)));
              }
            }
          } catch (error) {
            console.error('Error loading module content:', error);
          }
        }

        if (!isMounted) return;

        setModule(resolvedModule);

        const moduleIndex = modulesRef.current.findIndex(m => m.id === moduleId);
        setCurrentModuleIndex(moduleIndex >= 0 ? moduleIndex : 0);

        if (isAuthenticated && user) {
          if (!userProgressRef.current || userProgressRef.current.courseId !== courseId) {
            try {
              const progressData = await courseService.getUserProgress(courseId);
              if (isMounted) {
                userProgressRef.current = progressData;
                setUserProgress(progressData);
              }
            } catch (error) {
              console.error('Error loading user progress:', error);
            }
          }

          if (sessionModuleRef.current !== moduleId) {
            await endCurrentSession();

            try {
              const sessionId = await progressService.startLearningSession(courseId, moduleId);
              sessionIdRef.current = sessionId;
              sessionModuleRef.current = moduleId;
              if (isMounted) {
                setSessionStartTime(new Date());
              }
            } catch (error) {
              console.error('Error starting learning session:', error);
            }
          }
        }

        if (resolvedModule && resolvedModule.type === ModuleType.QUIZ) {
          loadQuizQuestions(resolvedModule);
        }
      } catch (error) {
        console.error('Error loading module data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadModuleData();

    return () => {
      isMounted = false;
    };
  }, [courseId, moduleId, isAuthenticated, user, navigate, endCurrentSession]);

  useEffect(() => {
    return () => {
      void endCurrentSession();
    };
  }, [endCurrentSession]);
  /**
   * Load quiz questions (mock data for now)
   */
  const loadQuizQuestions = (_module: Module) => {
    // Try to load quiz questions from module.content.quiz.questions (Firestore structure)
    let questions: QuizQuestion[] | undefined;
    let passingScore: number | undefined;
    if (_module.content && typeof _module.content === 'object' && _module.content !== null) {
      // Check for quiz map
      const quizMap = (_module.content as any)['quiz'];
      if (quizMap && typeof quizMap === 'object' && quizMap !== null) {
        if ('questions' in quizMap && Array.isArray(quizMap['questions'])) {
          questions = quizMap['questions'];
        }
        if ('passingScore' in quizMap && typeof quizMap['passingScore'] === 'number') {
          passingScore = quizMap['passingScore'];
        }
      }
      // Fallback: check for questions directly under content
      if (!questions && 'questions' in _module.content && Array.isArray((_module.content as any)['questions'])) {
        questions = (_module.content as any)['questions'];
      }
    }
    if (typeof passingScore === 'number') {
      setQuizPassPercentage(passingScore);
    } else {
      setQuizPassPercentage(70); // fallback default
    }
    if (questions && questions.length > 0) {
      setQuizQuestions(questions);
    } else {
      // Fallback to mock questions if none found
      const mockQuestions: QuizQuestion[] = [
        {
          id: '1',
          question: 'What is the main topic of this module?',
          options: [
            'Introduction to AI Ethics',
            'Machine Learning Basics',
            'Data Science Fundamentals',
            'Programming Concepts'
          ],
          correctAnswer: 0,
          explanation: 'This module focuses on introducing the core concepts of AI Ethics.'
        },
        {
          id: '2',
          question: 'Which of the following is a key ethical principle in AI?',
          options: [
            'Profit maximization',
            'Transparency and explainability',
            'Speed of processing',
            'Data collection volume'
          ],
          correctAnswer: 1,
          explanation: 'Transparency and explainability are fundamental ethical principles in AI development.'
        }
      ];
      setQuizQuestions(mockQuestions);
    }
  };

  /**
   * Check if user can access module
   */
  const canAccessModule = () => {
    if (!course) return false;
    if (course.isFree) return true;
    return canAccessTier(course.requiredSubscriptionTier as any);
  };

  /**
   * Navigate to next module
   */
  const goToNextModule = async () => {
    if (currentModuleIndex < modules.length - 1) {
      // End current session before navigating
      await endCurrentSession();
      const nextModule = modules[currentModuleIndex + 1];
      navigate(`/courses/${courseId}/modules/${nextModule.id}`);
    } else {
      // Last module: mark course as complete and navigate to catalog
      if (courseId && moduleId) {
        await progressService.updateModuleProgress(courseId, moduleId, 0, true);
      }
      await endCurrentSession();
      navigate('/courses');
    }
  };

  /**
   * Navigate to previous module
   */
  const goToPreviousModule = async () => {
    if (currentModuleIndex > 0) {
      // Reset course review view if active
      if (showCourseReview) {
        setShowCourseReview(false);
      }
      
      // End current session before navigating
      await endCurrentSession();
      
      const prevModule = modules[currentModuleIndex - 1];
      navigate(`/courses/${courseId}/modules/${prevModule.id}`);
    }
  };

  /**
   * Mark module as completed
   */
  const markModuleCompleted = async () => {
    if (!module || !isAuthenticated || !courseId) return;

    try {
      // Calculate time spent in this session
      const timeSpent = sessionStartTime 
        ? Math.round((new Date().getTime() - sessionStartTime.getTime()) / 1000 / 60)
        : module.estimatedDuration;

      // Update progress using the new progress service
      await progressService.updateModuleProgress(courseId, module.id, timeSpent, true);
      
      // End current session as completed
      await endCurrentSession();

      // Wait briefly to ensure Firestore update propagates
      await new Promise(res => setTimeout(res, 500));

      // Reload progress data
      const progressData = await courseService.getUserProgress(courseId);
      setUserProgress(progressData);
    } catch (error) {
      console.error('Error marking module as completed:', error);
    }
  };

  /**
   * Handle quiz answer selection
   */
  const handleQuizAnswer = (questionIndex: number, answer: any) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  /**
   * Submit quiz
   */
  const submitQuiz = async () => {
    if (!module || !courseId) return;

    let correct = 0;
    quizQuestions.forEach((question, index) => {
      const userAnswer = selectedAnswers[index];
      const correctAnswer = question.correctAnswer;
      
      // Handle different question types
      const questionType = question.type || 'multiple_choice';
      
      switch (questionType) {
        case 'multiple_choice':
          if (userAnswer === correctAnswer) correct++;
          break;
        case 'true_false':
          if (userAnswer === correctAnswer) correct++;
          break;
        case 'short_answer':
          if (Array.isArray(correctAnswer)) {
            // Multiple acceptable answers
            const normalizedUserAnswer = String(userAnswer).toLowerCase().trim();
            const normalizedCorrectAnswers = correctAnswer.map(ans => String(ans).toLowerCase().trim());
            if (normalizedCorrectAnswers.includes(normalizedUserAnswer)) correct++;
          } else {
            // Single answer
            const normalizedUserAnswer = String(userAnswer).toLowerCase().trim();
            const normalizedCorrectAnswer = String(correctAnswer).toLowerCase().trim();
            if (normalizedUserAnswer === normalizedCorrectAnswer) correct++;
          }
          break;
        case 'multiple_response':
          if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
            // Check if arrays are equal (same elements, same order)
            const sortedUserAnswer = [...userAnswer].sort();
            const sortedCorrectAnswer = [...correctAnswer].sort();
            if (JSON.stringify(sortedUserAnswer) === JSON.stringify(sortedCorrectAnswer)) correct++;
          }
          break;
        case 'matching':
          if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
            // Check if all pairings are correct
            let allCorrect = true;
            for (let i = 0; i < correctAnswer.length; i++) {
              if (userAnswer[i] !== correctAnswer[i]) {
                allCorrect = false;
                break;
              }
            }
            if (allCorrect) correct++;
          }
          break;
        default:
          // Fallback to simple equality check
          if (userAnswer === correctAnswer) correct++;
      }
    });
    
    const score = (correct / quizQuestions.length) * 100;
    setQuizScore(score);
    setShowResults(true);
    
    // Calculate time spent on quiz
    const timeSpent = sessionStartTime 
      ? Math.round((new Date().getTime() - sessionStartTime.getTime()) / 1000 / 60)
      : 10; // Default 10 minutes if no session time

    // Record quiz attempt using progress service
    if (isAuthenticated) {
      await progressService.recordQuizAttempt(
        courseId,
        module.id,
        selectedAnswers,
        correct,
        quizQuestions.length,
        timeSpent
      );
    }

    // End session as completed if quiz passed
    if (score >= quizPassPercentage) {
      await endCurrentSession();
      
      // Reload progress data
      const progressData = await courseService.getUserProgress(courseId);
      setUserProgress(progressData);
    }
  };

  /**
   * Reset quiz
   */
  const resetQuiz = () => {
    setSelectedAnswers({});
    setCurrentQuestionIndex(0);
    setShowResults(false);
    setShowAnswers(false);
    setQuizScore(0);
  };

  /**
   * Show quiz answers
   */
  const showQuizAnswers = () => {
    setShowAnswers(true);
  };

  /**
   * Format duration
   */
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  /**
   * Get module type icon
   */
  const getModuleTypeIcon = (type: ModuleType) => {
    switch (type) {
      case ModuleType.VIDEO:
        return <Video className="w-5 h-5" />;
      case ModuleType.AUDIO:
        return <Headphones className="w-5 h-5" />;
      case ModuleType.PDF:
        return <FileText className="w-5 h-5" />;
      case ModuleType.TEXT:
        return <BookOpen className="w-5 h-5" />;
      case ModuleType.QUIZ:
        return <HelpCircle className="w-5 h-5" />;
      default:
        return <BookOpen className="w-5 h-5" />;
    }
  };

  /**
   * Render video player
   */
  const renderVideoPlayer = () => {
    const videoUrl = module?.content?.videoUrl;

    if (!videoUrl) {
      return (
        <div className="space-y-4">
          <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center text-white">
                <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">No Video Available</p>
                <p className="text-sm opacity-75">Video content is not available for this module</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
          <video
            className="w-full h-full"
            controls
            preload="metadata"
          >
            <source src={videoUrl} type="video/mp4" />
            <source src={videoUrl} type="video/webm" />
            <source src={videoUrl} type="video/ogg" />
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Video transcript if available */}
        {module?.content?.videoTranscript && (
          <div>
            <details className="bg-neutral-50 rounded-lg p-4">
              <summary className="cursor-pointer font-medium text-neutral-800 mb-2">
                Video Transcript
              </summary>
              <div className="text-sm text-neutral-700 whitespace-pre-wrap">
                {module.content.videoTranscript}
              </div>
            </details>
          </div>
        )}
      </div>
    );
  };

  /**
   * Render audio player
   */
  const renderAudioPlayer = () => {
    const audioUrl = module?.content?.audioUrl;
    
    if (!audioUrl) {
      return (
        <div className="text-center space-y-4">
          <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
            <Headphones className="w-12 h-12 text-primary" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-neutral-800 mb-2">No Audio Available</h3>
            <p className="text-neutral-600">Audio content is not available for this module</p>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center space-y-6">
        <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
          <Headphones className="w-12 h-12 text-primary" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-neutral-800 mb-2">Audio Content</h3>
          <p className="text-neutral-600">Listen to the audio content for this module</p>
        </div>

        {/* Audio Player */}
        <div>
          <audio
            className="w-full max-w-md mx-auto"
            controls
            preload="metadata"
          >
            <source src={audioUrl} type="audio/mpeg" />
            <source src={audioUrl} type="audio/wav" />
            <source src={audioUrl} type="audio/ogg" />
            Your browser does not support the audio element.
          </audio>
        </div>

        <p className="text-sm text-neutral-500">Duration: {formatDuration(module?.estimatedDuration || 0)}</p>

        {/* Audio transcript if available */}
        {module?.content?.audioTranscript && (
          <div>
            <details className="bg-neutral-50 rounded-lg p-4 text-left">
              <summary className="cursor-pointer font-medium text-neutral-800 mb-2 text-center">
                Audio Transcript
              </summary>
              <div className="text-sm text-neutral-700 whitespace-pre-wrap">
                {module.content.audioTranscript}
              </div>
            </details>
          </div>
        )}
      </div>
    );
  };

  /**
   * Render PDF viewer
   */
  const renderPDFViewer = () => {
    const pdfUrl = module?.content?.pdfUrl;
    const pdfTitle = module?.content?.pdfTitle || module?.title || 'PDF Document';
    
    if (!pdfUrl) {
      return (
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center mx-auto">
            <FileText className="w-8 h-8 text-red-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-neutral-800 mb-2">No PDF Available</h3>
            <p className="text-neutral-600">PDF content is not available for this module</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center mx-auto">
            <FileText className="w-8 h-8 text-red-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-neutral-800 mb-2">{pdfTitle}</h3>
            <p className="text-neutral-600">Read the PDF content for this module</p>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 p-4 bg-neutral-100 border-b border-neutral-200">
            <Button
              variant="outline"
              size="sm"
              icon={Download}
              onClick={() => {
                const link = document.createElement('a');
                link.href = pdfUrl;
                link.download = pdfTitle;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(pdfUrl, '_blank')}
            >
              Open in New Tab
            </Button>
          </div>

          {/* PDF Embed */}
          <div className="relative" style={{ height: '600px' }}>
            <iframe
              src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
              className="w-full h-full border-0"
              title={pdfTitle}
              onError={() => {
                // Fallback if iframe fails to load
                console.warn('PDF iframe failed to load, showing download option');
              }}
            />
          </div>
        </div>

        {/* PDF Info */}
        <div className="text-center space-y-2">
          <p className="text-sm text-neutral-500">
            {module?.content?.pdfPageCount && `${module.content.pdfPageCount} pages • `}
            Duration: {formatDuration(module?.estimatedDuration || 0)}
          </p>
          <p className="text-xs text-neutral-400">
            If the PDF doesn't display properly, try downloading it or opening in a new tab.
          </p>
        </div>
      </div>
    );
  };

  /**
   * Render text content
   */
  const renderTextContent = () => {
    let content = '';
    let isHtml = false;
    if (module?.content) {
      if (typeof module.content === 'string') {
        content = module.content;
      } else if (typeof module.content === 'object' && module.content !== null) {
        if ('html' in module.content && typeof (module.content as any)['html'] === 'string') {
          content = (module.content as any)['html'];
          isHtml = true;
        } else if ('text' in module.content && typeof (module.content as any)['text'] === 'string') {
          content = (module.content as any)['text'];
        } else {
          content = JSON.stringify(module.content);
        }
      }
    }
    return (
      <div className="space-y-4">
        {/* Custom Markdown table styles for clarity */}
        <style>{`
          .prose table {
            border-collapse: collapse;
            width: 100%;
            margin-bottom: 1.5em;
          }
          .prose th, .prose td {
            border: 2px solid #5B6CFF;
            padding: 0.75em 1em;
            text-align: left;
          }
          .prose tr:nth-child(even) td {
            background-color: #f3f4f6;
          }
          .prose th {
            background-color: #e5e7eb;
            font-weight: 600;
          }
        `}</style>
        <div className="prose prose-neutral max-w-none">
          <div className="text-neutral-700 leading-relaxed space-y-4">
            {content ? (
              isHtml ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
              )
            ) : (
              <p>No content available for this module.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render quiz content
   */
  const renderQuizContent = () => {
    // Show answers view
    if (showAnswers) {
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-2xl font-bold text-neutral-800 mb-2">Quiz Answers</h3>
            <p className="text-neutral-600">
              Review the correct answers and explanations below.
            </p>
          </div>

          <div className="space-y-6">
            {quizQuestions.map((question, questionIndex) => {
              const userAnswer = selectedAnswers[questionIndex];
              const correctAnswer = question.correctAnswer;
              const questionType = question.type || 'multiple_choice';
              
              // Determine if answer is correct based on question type
              let isCorrect = false;
              switch (questionType) {
                case 'multiple_choice':
                case 'true_false':
                  isCorrect = userAnswer === correctAnswer;
                  break;
                case 'short_answer':
                  if (Array.isArray(correctAnswer)) {
                    const normalizedUserAnswer = String(userAnswer).toLowerCase().trim();
                    const normalizedCorrectAnswers = correctAnswer.map(ans => String(ans).toLowerCase().trim());
                    isCorrect = normalizedCorrectAnswers.includes(normalizedUserAnswer);
                  } else {
                    const normalizedUserAnswer = String(userAnswer).toLowerCase().trim();
                    const normalizedCorrectAnswer = String(correctAnswer).toLowerCase().trim();
                    isCorrect = normalizedUserAnswer === normalizedCorrectAnswer;
                  }
                  break;
                case 'multiple_response':
                  if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
                    const sortedUserAnswer = [...userAnswer].sort();
                    const sortedCorrectAnswer = [...correctAnswer].sort();
                    isCorrect = JSON.stringify(sortedUserAnswer) === JSON.stringify(sortedCorrectAnswer);
                  }
                  break;
                case 'matching':
                  if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
                    isCorrect = true;
                    for (let i = 0; i < correctAnswer.length; i++) {
                      if (userAnswer[i] !== correctAnswer[i]) {
                        isCorrect = false;
                        break;
                      }
                    }
                  }
                  break;
                default:
                  isCorrect = userAnswer === correctAnswer;
              }
              
              return (
                <div key={question.id} className="border border-neutral-200 rounded-lg p-6">
                  <div className="mb-4">
                    <h4 className="text-lg font-medium text-neutral-800 mb-3">
                      Question {questionIndex + 1}: {question.question}
                    </h4>
                    
                    {/* Render answer display based on question type */}
                    {(() => {
                      switch (questionType) {
                        case 'multiple_choice':
                          return (
                            <div className="space-y-2">
                              {question.options.map((option, optionIndex) => {
                                const isUserAnswer = userAnswer === optionIndex;
                                const isCorrectAnswer = optionIndex === correctAnswer;
                                
                                let className = "p-3 rounded-lg border-2 ";
                                if (isCorrectAnswer) {
                                  className += "border-green-500 bg-green-50 text-green-800";
                                } else if (isUserAnswer && !isCorrect) {
                                  className += "border-red-500 bg-red-50 text-red-800";
                                } else {
                                  className += "border-neutral-200 bg-neutral-50 text-neutral-700";
                                }
                                
                                return (
                                  <div key={optionIndex} className={className}>
                                    <div className="flex items-center justify-between">
                                      <span>
                                        <span className="font-medium mr-3">
                                          {String.fromCharCode(65 + optionIndex)}.
                                        </span>
                                        {option}
                                      </span>
                                      <div className="flex items-center space-x-2">
                                        {isCorrectAnswer && (
                                          <span className="text-green-600 text-sm font-medium">
                                            ✓ Correct
                                          </span>
                                        )}
                                        {isUserAnswer && !isCorrect && (
                                          <span className="text-red-600 text-sm font-medium">
                                            Your answer
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                          
                        case 'true_false':
                          return (
                            <div className="space-y-2">
                              {[true, false].map((value, index) => {
                                const isUserAnswer = userAnswer === value;
                                const isCorrectAnswer = value === correctAnswer;
                                
                                let className = "p-3 rounded-lg border-2 ";
                                if (isCorrectAnswer) {
                                  className += "border-green-500 bg-green-50 text-green-800";
                                } else if (isUserAnswer && !isCorrect) {
                                  className += "border-red-500 bg-red-50 text-red-800";
                                } else {
                                  className += "border-neutral-200 bg-neutral-50 text-neutral-700";
                                }
                                
                                return (
                                  <div key={index} className={className}>
                                    <div className="flex items-center justify-between">
                                      <span>
                                        <span className="font-medium mr-3">
                                          {value ? 'A.' : 'B.'}
                                        </span>
                                        {value ? 'True' : 'False'}
                                      </span>
                                      <div className="flex items-center space-x-2">
                                        {isCorrectAnswer && (
                                          <span className="text-green-600 text-sm font-medium">
                                            ✓ Correct
                                          </span>
                                        )}
                                        {isUserAnswer && !isCorrect && (
                                          <span className="text-red-600 text-sm font-medium">
                                            Your answer
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                          
                        case 'short_answer':
                          return (
                            <div className="space-y-3">
                              <div className="p-3 bg-neutral-50 rounded-lg border">
                                <div className="text-sm text-neutral-600 mb-1">Your Answer:</div>
                                <div className={`font-medium ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                  {userAnswer || '(No answer provided)'}
                                </div>
                              </div>
                              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                <div className="text-sm text-green-600 mb-1">Correct Answer(s):</div>
                                <div className="font-medium text-green-700">
                                  {Array.isArray(correctAnswer) ? correctAnswer.join(', ') : String(correctAnswer)}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {isCorrect ? (
                                  <span className="text-green-600 text-sm font-medium">
                                    ✓ Correct
                                  </span>
                                ) : (
                                  <span className="text-red-600 text-sm font-medium">
                                    ✗ Incorrect
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                          
                        case 'multiple_response':
                          return (
                            <div className="space-y-2">
                              {question.options.map((option, optionIndex) => {
                                const isUserAnswer = Array.isArray(userAnswer) && userAnswer.includes(optionIndex);
                                const isCorrectAnswer = Array.isArray(correctAnswer) && (correctAnswer as number[]).includes(optionIndex);
                                
                                let className = "p-3 rounded-lg border-2 ";
                                if (isCorrectAnswer && isUserAnswer) {
                                  className += "border-green-500 bg-green-50 text-green-800";
                                } else if (isCorrectAnswer && !isUserAnswer) {
                                  className += "border-orange-500 bg-orange-50 text-orange-800";
                                } else if (!isCorrectAnswer && isUserAnswer) {
                                  className += "border-red-500 bg-red-50 text-red-800";
                                } else {
                                  className += "border-neutral-200 bg-neutral-50 text-neutral-700";
                                }
                                
                                return (
                                  <div key={optionIndex} className={className}>
                                    <div className="flex items-center justify-between">
                                      <span>
                                        <span className="font-medium mr-3">
                                          {String.fromCharCode(65 + optionIndex)}.
                                        </span>
                                        {option}
                                      </span>
                                      <div className="flex items-center space-x-2">
                                        {isCorrectAnswer && isUserAnswer && (
                                          <span className="text-green-600 text-sm font-medium">
                                            ✓ Correct selection
                                          </span>
                                        )}
                                        {isCorrectAnswer && !isUserAnswer && (
                                          <span className="text-orange-600 text-sm font-medium">
                                            Should be selected
                                          </span>
                                        )}
                                        {!isCorrectAnswer && isUserAnswer && (
                                          <span className="text-red-600 text-sm font-medium">
                                            Incorrectly selected
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                          
                        case 'matching':
                          return (
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <h5 className="font-medium text-neutral-700 mb-3">Items:</h5>
                                  <div className="space-y-2">
                                    {question.leftItems?.map((item, index) => (
                                      <div key={index} className="p-3 bg-neutral-50 rounded-lg border">
                                        <span className="font-medium mr-2">{index + 1}.</span>
                                        {item}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <h5 className="font-medium text-neutral-700 mb-3">Your Matches vs Correct:</h5>
                                  <div className="space-y-2">
                                    {question.leftItems?.map((leftItem, leftIndex) => {
                                      const userMatch = Array.isArray(userAnswer) ? userAnswer[leftIndex] : undefined;
                                      const correctMatch = Array.isArray(correctAnswer) ? correctAnswer[leftIndex] : undefined;
                                      const isMatchCorrect = userMatch === correctMatch;
                                      
                                      return (
                                        <div key={leftIndex} className={`p-3 rounded-lg border-2 ${
                                          isMatchCorrect ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
                                        }`}>
                                          <div className="text-sm">
                                            <span className="font-medium">{leftIndex + 1}.</span>
                                            <div className="mt-1">
                                              <div className={isMatchCorrect ? 'text-green-700' : 'text-red-700'}>
                                                Your match: {userMatch !== undefined && question.rightItems?.[userMatch] 
                                                  ? `${String.fromCharCode(65 + userMatch)}. ${question.rightItems[userMatch]}`
                                                  : '(No match selected)'}
                                              </div>
                                              {!isMatchCorrect && (
                                                <div className="text-green-700 mt-1">
                                                  Correct match: {correctMatch !== undefined && typeof correctMatch === 'number' && question.rightItems?.[correctMatch]
                                                    ? `${String.fromCharCode(65 + correctMatch)}. ${question.rightItems[correctMatch]}`
                                                    : '(No correct match)'}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                          
                        default:
                          // Fallback to multiple choice display
                          return (
                            <div className="space-y-2">
                              {question.options.map((option, optionIndex) => {
                                const isUserAnswer = userAnswer === optionIndex;
                                const isCorrectAnswer = optionIndex === correctAnswer;
                                
                                let className = "p-3 rounded-lg border-2 ";
                                if (isCorrectAnswer) {
                                  className += "border-green-500 bg-green-50 text-green-800";
                                } else if (isUserAnswer && !isCorrect) {
                                  className += "border-red-500 bg-red-50 text-red-800";
                                } else {
                                  className += "border-neutral-200 bg-neutral-50 text-neutral-700";
                                }
                                
                                return (
                                  <div key={optionIndex} className={className}>
                                    <div className="flex items-center justify-between">
                                      <span>
                                        <span className="font-medium mr-3">
                                          {String.fromCharCode(65 + optionIndex)}.
                                        </span>
                                        {option}
                                      </span>
                                      <div className="flex items-center space-x-2">
                                        {isCorrectAnswer && (
                                          <span className="text-green-600 text-sm font-medium">
                                            ✓ Correct
                                          </span>
                                        )}
                                        {isUserAnswer && !isCorrect && (
                                          <span className="text-red-600 text-sm font-medium">
                                            Your answer
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                      }
                    })()}
                    
                    {question.explanation && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">Explanation:</h5>
                        <p className="text-blue-700 text-sm">{question.explanation}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="pt-4 flex justify-center space-x-4">
            <Button variant="outline" onClick={() => setShowAnswers(false)}>
              Back to Results
            </Button>
            <Button variant="outline" icon={RotateCcw} onClick={resetQuiz}>
              Retake Quiz
            </Button>
          </div>
        </div>
      );
    }

    // Quiz results view
    if (showResults) {
      return (
        <div className="space-y-6 text-center">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto ${
            quizScore >= quizPassPercentage ? 'bg-green-100' : 'bg-red-100'
          }`}>
            <CheckCircle className={`w-12 h-12 ${
              quizScore >= quizPassPercentage ? 'text-green-600' : 'text-red-600'
            }`} />
          </div>
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-neutral-800">Quiz Complete!</h3>
            <p className="text-lg text-neutral-600">
              Your Score: {quizScore.toFixed(0)}% ({Math.round(quizScore / 100 * quizQuestions.length)}/{quizQuestions.length})
            </p>
            <p className={`text-sm ${
              quizScore >= quizPassPercentage ? 'text-green-600' : 'text-red-600'
            }`}>
              {quizScore >= quizPassPercentage
                ? 'Congratulations! You passed the quiz.'
                : `You need ${quizPassPercentage}% to pass. Try again!`}
            </p>
          </div>
          <div className="flex justify-center space-x-4">
            <Button variant="outline" icon={Eye} onClick={showQuizAnswers}>
              Show Answers
            </Button>
            <Button variant="outline" icon={RotateCcw} onClick={resetQuiz}>
              Retake Quiz
            </Button>
          </div>
        </div>
      );
    }

    const currentQuestion = quizQuestions[currentQuestionIndex];
    if (!currentQuestion) return null;

    return (
      <div className="space-y-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-neutral-800">
              Question {currentQuestionIndex + 1} of {quizQuestions.length}
            </h3>
            <span className="text-sm text-neutral-500">
              {Math.round(((currentQuestionIndex + 1) / quizQuestions.length) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-neutral-200 rounded-full h-2 mb-6">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestionIndex + 1) / quizQuestions.length) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="mb-8">
          <h4 className="text-lg font-medium text-neutral-800 mb-6">
            {currentQuestion.question}
          </h4>
          
          {/* Render different question types */}
          {(() => {
            const questionType = currentQuestion.type || 'multiple_choice';
            
            switch (questionType) {
              case 'multiple_choice':
                return (
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuizAnswer(currentQuestionIndex, index)}
                        className={`w-full p-4 text-left rounded-lg border-2 transition-colors ${
                          selectedAnswers[currentQuestionIndex] === index
                            ? 'border-primary bg-primary-50 text-primary-800'
                            : 'border-neutral-200 hover:border-neutral-300 text-neutral-700'
                        }`}
                      >
                        <span className="font-medium mr-3">
                          {String.fromCharCode(65 + index)}.
                        </span>
                        {option}
                      </button>
                    ))}
                  </div>
                );
                
              case 'true_false':
                return (
                  <div className="space-y-3">
                    {[true, false].map((value, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuizAnswer(currentQuestionIndex, value)}
                        className={`w-full p-4 text-left rounded-lg border-2 transition-colors ${
                          selectedAnswers[currentQuestionIndex] === value
                            ? 'border-primary bg-primary-50 text-primary-800'
                            : 'border-neutral-200 hover:border-neutral-300 text-neutral-700'
                        }`}
                      >
                        <span className="font-medium mr-3">
                          {value ? 'A.' : 'B.'}
                        </span>
                        {value ? 'True' : 'False'}
                      </button>
                    ))}
                  </div>
                );
                
              case 'short_answer':
                return (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={selectedAnswers[currentQuestionIndex] || ''}
                      onChange={(e) => handleQuizAnswer(currentQuestionIndex, e.target.value)}
                      placeholder={currentQuestion.blankPlaceholder || "Type your answer here"}
                      className="w-full p-4 border-2 border-neutral-200 rounded-lg focus:border-primary focus:outline-none"
                    />
                  </div>
                );
                
              case 'multiple_response':
                return (
                  <div className="space-y-3">
                    <p className="text-sm text-neutral-600 mb-3">Select all correct answers:</p>
                    {currentQuestion.options.map((option, index) => {
                      const currentAnswers = selectedAnswers[currentQuestionIndex] || [];
                      const isSelected = Array.isArray(currentAnswers) && currentAnswers.includes(index);
                      
                      return (
                        <button
                          key={index}
                          onClick={() => {
                            const currentAnswers = selectedAnswers[currentQuestionIndex] || [];
                            let newAnswers;
                            if (Array.isArray(currentAnswers)) {
                              if (currentAnswers.includes(index)) {
                                newAnswers = currentAnswers.filter(i => i !== index);
                              } else {
                                newAnswers = [...currentAnswers, index].sort();
                              }
                            } else {
                              newAnswers = [index];
                            }
                            handleQuizAnswer(currentQuestionIndex, newAnswers);
                          }}
                          className={`w-full p-4 text-left rounded-lg border-2 transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary-50 text-primary-800'
                              : 'border-neutral-200 hover:border-neutral-300 text-neutral-700'
                          }`}
                        >
                          <span className="font-medium mr-3">
                            {isSelected ? '☑' : '☐'} {String.fromCharCode(65 + index)}.
                          </span>
                          {option}
                        </button>
                      );
                    })}
                  </div>
                );
                
              case 'matching':
                return (
                  <div className="space-y-4">
                    <p className="text-sm text-neutral-600 mb-3">Match each item on the left with the correct item on the right:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h5 className="font-medium text-neutral-700 mb-3">Items to Match:</h5>
                        <div className="space-y-2">
                          {currentQuestion.leftItems?.map((item, index) => (
                            <div key={index} className="p-3 bg-neutral-50 rounded-lg border">
                              <span className="font-medium mr-2">{index + 1}.</span>
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-neutral-700 mb-3">Your Matches:</h5>
                        <div className="space-y-2">
                          {currentQuestion.leftItems?.map((leftItem, leftIndex) => {
                            const currentAnswers = selectedAnswers[currentQuestionIndex] || [];
                            const selectedRightIndex = Array.isArray(currentAnswers) ? currentAnswers[leftIndex] : undefined;
                            
                            return (
                              <div key={leftIndex} className="flex items-center space-x-2">
                                <span className="w-8 text-center font-medium">{leftIndex + 1}.</span>
                                <select
                                  value={selectedRightIndex !== undefined ? selectedRightIndex : ''}
                                  onChange={(e) => {
                                    const newAnswers = Array.isArray(currentAnswers) ? [...currentAnswers] : Array(currentQuestion.leftItems?.length || 0).fill(null);
                                    newAnswers[leftIndex] = parseInt(e.target.value, 10);
                                    handleQuizAnswer(currentQuestionIndex, newAnswers);
                                  }}
                                  className="flex-1 p-2 border border-neutral-300 rounded focus:border-primary focus:outline-none"
                                >
                                  <option value="">Select a match...</option>
                                  {currentQuestion.rightItems?.map((rightItem, rightIndex) => (
                                    <option key={rightIndex} value={rightIndex}>
                                      {String.fromCharCode(65 + rightIndex)}. {rightItem}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
                
              default:
                // Fallback to multiple choice
                return (
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuizAnswer(currentQuestionIndex, index)}
                        className={`w-full p-4 text-left rounded-lg border-2 transition-colors ${
                          selectedAnswers[currentQuestionIndex] === index
                            ? 'border-primary bg-primary-50 text-primary-800'
                            : 'border-neutral-200 hover:border-neutral-300 text-neutral-700'
                        }`}
                      >
                        <span className="font-medium mr-3">
                          {String.fromCharCode(65 + index)}.
                        </span>
                        {option}
                      </button>
                    ))}
                  </div>
                );
            }
          })()}
        </div>

        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
            disabled={currentQuestionIndex === 0}
          >
            Previous
          </Button>
          
          {currentQuestionIndex === quizQuestions.length - 1 ? (
            <Button
              variant="primary"
              icon={Send}
              onClick={submitQuiz}
              disabled={Object.keys(selectedAnswers).length !== quizQuestions.length}
            >
              Submit Quiz
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
              disabled={selectedAnswers[currentQuestionIndex] === undefined}
            >
              Next
            </Button>
          )}
        </div>
      </div>
    );
  };

  /**
   * Render course review module
   */
  const renderCourseReview = () => {
    if (!course) return null;

    const handleReviewSubmit = async (
      feedback: Omit<CourseReviewFeedback, 'id' | 'courseId' | 'userId' | 'createdAt' | 'updatedAt'>
    ) => {
      setIsSubmittingReview(true);
      
      try {
        // Submit the review
        await courseService.submitCourseReview(courseId!, feedback);
        
        // Mark the last module as completed
        if (module) {
          const timeSpent = sessionStartTime 
            ? Math.round((new Date().getTime() - sessionStartTime.getTime()) / 1000 / 60)
            : module.estimatedDuration;
          
          await progressService.updateModuleProgress(courseId!, module.id, timeSpent, true);
        }
        
        // End current session as completed
        await endCurrentSession();
        
        // Wait briefly to ensure Firestore update propagates
        await new Promise(res => setTimeout(res, 500));
        
        // Navigate directly to certificate page
        navigate(`/certificate/${courseId}`);
      } catch (error) {
        console.error('Error submitting course review:', error);
        alert('Failed to submit review. Please try again.');
      } finally {
        setIsSubmittingReview(false);
      }
    };

    return (
      <CourseReviewModule
        course={course}
        onSubmit={handleReviewSubmit}
        isSubmitting={isSubmittingReview}
      />
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!course || !module) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-neutral-800 mb-4">Module Not Found</h1>
          <Button variant="outline" onClick={() => navigate(ROUTES.courses)}>
            Back to Courses
          </Button>
        </div>
      </Layout>
    );
  }

  if (!canAccessModule()) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-neutral-800 mb-4">Access Restricted</h1>
          <p className="text-neutral-600 mb-6">
            This module requires a {course.requiredSubscriptionTier} subscription to access.
          </p>
          <Button variant="primary" onClick={() => navigate(ROUTES.pricing)}>
            Upgrade Subscription
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
            <div className="flex-1">
              <div className="flex flex-col gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  icon={ArrowLeft}
                  onClick={() => navigate(`/courses/${courseId}`)}
                  className="w-fit"
                >
                  Back to Courses
                </Button>
                <div className="flex flex-col gap-1">
                  <h1 className="font-montserrat font-bold text-[24px] text-neutral-900 leading-tight">
                    {course.title}
                  </h1>
                  <h2 className="font-montserrat font-normal text-[20px] text-neutral-700">
                    {module.title}
                  </h2>
                </div>
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-2 text-sm text-neutral-500 mt-2 sm:mt-1">
              {getModuleTypeIcon(module.type)}
              <span className="capitalize">{module.type}</span>
              <span>•</span>
              <Clock className="w-4 h-4" />
              <span>{formatDuration(module.estimatedDuration)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Module Content OR Course Review */}
              <div className="mb-8">
                {!showCourseReview ? (
                  <Card className="p-6 sm:p-8 space-y-6 sm:space-y-8">
                    <div className="space-y-2">
                      <p className="font-montserrat font-bold text-[18px] text-neutral-800">
                        About this module
                      </p>
                      <p className="font-open-sans font-normal text-[16px] text-neutral-700 leading-relaxed">
                        {module.description || 'Details for this module will be available soon.'}
                      </p>
                    </div>

                    <div>
                      {module.type === ModuleType.VIDEO && renderVideoPlayer()}
                      {module.type === ModuleType.AUDIO && renderAudioPlayer()}
                      {module.type === ModuleType.PDF && renderPDFViewer()}
                      {module.type === ModuleType.TEXT && renderTextContent()}
                      {module.type === ModuleType.QUIZ && renderQuizContent()}
                    </div>
                  </Card>
                ) : (
                  renderCourseReview()
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  icon={ArrowLeft}
                  onClick={goToPreviousModule}
                  disabled={currentModuleIndex === 0}
                >
                  Previous Module
                </Button>
                
                <div className="flex space-x-4">
                  {currentModuleIndex === modules.length - 1 ? (
                    !showCourseReview ? (
                      <Button
                        variant="primary"
                        icon={Star}
                        onClick={async () => {
                          // Check if user has already submitted a review
                          const hasReview = await courseService.hasCompletedReview(courseId!);
                          if (hasReview) {
                            // If already reviewed, complete normally
                            await markModuleCompleted();
                            navigate(`/certificate/${courseId}`);
                          } else {
                            // Show review form (don't mark module complete yet)
                            setShowCourseReview(true);
                          }
                        }}
                        disabled={
                          module.type === ModuleType.QUIZ
                            ? quizScore < quizPassPercentage
                            : false
                        }
                      >
                        Continue to Course Review
                      </Button>
                    ) : null
                  ) : (
                    <Button
                      variant="primary"
                      icon={ArrowRight}
                      onClick={async () => {
                        // Mark current module as complete before navigating
                        await markModuleCompleted();
                        await goToNextModule();
                      }}
                      disabled={module.type === ModuleType.QUIZ && quizScore < quizPassPercentage}
                    >
                      Next Module
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-6">
                {/* Progress */}
                {userProgress && (
                  <Card className="p-4">
                    <h3 className="font-semibold text-neutral-800 mb-3">Course Progress</h3>
                    <div className="mb-3">
                      <div className="flex justify-between text-sm text-neutral-600 mb-1">
                        <span>Completion</span>
                        <span>{Math.round(userProgress.completionPercentage)}%</span>
                      </div>
                      <div className="w-full bg-neutral-200 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${userProgress.completionPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600">
                      {userProgress.completedModules} of {userProgress.totalModules} modules completed
                    </p>
                    <p className="text-xs text-neutral-500 mt-1">
                      + Course Review (final step)
                    </p>
                  </Card>
                )}

                {/* Module List with checkmarks for completed modules */}
                <Card className="p-4">
                  <h3 className="font-semibold text-neutral-800 mb-3">Course Modules</h3>
                  <div className="space-y-2">
                    {modules.map((mod, index) => {
                      const progress = userProgress?.moduleProgress?.find(mp => mp.moduleId === mod.id);
                      const isCompleted = progress?.status === 'completed';
                      const isCurrentModule = mod.id === moduleId && !showCourseReview;
                      return (
                        <button
                          key={mod.id}
                          onClick={() => {
                            // Reset course review view when navigating to a different module
                            if (showCourseReview) {
                              setShowCourseReview(false);
                            }
                            navigate(`/courses/${courseId}/modules/${mod.id}`);
                          }}
                          className={`w-full p-3 text-left rounded-lg transition-colors ${
                            isCurrentModule
                              ? 'bg-primary-50 border border-primary-200 text-primary-800'
                              : 'hover:bg-neutral-50 text-neutral-700'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              {getModuleTypeIcon(mod.type)}
                              <span className="text-sm font-medium truncate">
                                {index + 1}. {mod.title}
                              </span>
                            </div>
                            {isCompleted && (
                              <CheckCircle className={`w-4 h-4 ${isCurrentModule ? 'text-primary' : 'text-blue-400'} flex-shrink-0`} />
                            )}
                          </div>
                        </button>
                      );
                    })}
                    
                    {/* Virtual Course Review Module */}
                    <button
                      onClick={async () => {
                        // If not on last module, navigate to it first
                        if (currentModuleIndex < modules.length - 1) {
                          const lastModule = modules[modules.length - 1];
                          navigate(`/courses/${courseId}/modules/${lastModule.id}`);
                        } else if (!showCourseReview) {
                          // On last module but review not shown yet
                          const hasReview = await courseService.hasCompletedReview(courseId!);
                          if (!hasReview) {
                            await markModuleCompleted();
                            setShowCourseReview(true);
                          }
                        }
                      }}
                      className={`w-full p-3 text-left rounded-lg transition-colors ${
                        showCourseReview
                          ? 'bg-primary-50 border border-primary-200 text-primary-800'
                          : 'hover:bg-neutral-50 text-neutral-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Star className="w-4 h-4" />
                          <span className="text-sm font-medium truncate">
                            {modules.length + 1}. Course Review
                          </span>
                        </div>
                        {showCourseReview && (
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
